import * as React from 'react';
import { RouteComponentProps, Link } from 'react-router-dom';
import Page from '../../components/Page';
import * as dateFormat from 'dateformat';
import Section from '../../components/Section';
import { Text, TextArea } from '../../components/forms';
import { Order, NewFulfillment } from '../../order';
import { ActionBar, ActionBarStyle } from '../../components/ActionBar';
import { ButtonStyle } from '../../components/Button';
import { useRequestAsync, getHeaders } from '../../api/client';
import { ProfileContext } from '../../profile';
import Loader from '../../components/Loader';
import Flash, { ErrorFlash } from '../../components/Flash';

interface Props extends RouteComponentProps<any> {}

const DATE_FORMAT = 'd mmmm, yyyy - HH:MM:s';

const getStateEntity = (props: RouteComponentProps<any>): Order => {
  return props.location.state ? props.location.state.order : null;
};

export default function ShipOrderShipment(props: Props) {
  const context = React.useContext(ProfileContext);
  const order: Order = getStateEntity(props);
  const [fulfillment, setFulfillment] = React.useState({
    carrier_name: order.shipments[0].shipping_method.name,
    tracking_code: '',
    tracking_url: '',
    notes: '',
    price: order.shipments[0].shipping_method.price_amount,
    price_currency: order.shipments[0].shipping_method.price_currency,
  });

  const [state, submit] = useRequestAsync((data: NewFulfillment) => ({
    url: `/store/orders/${order.id}/shipments/${order.shipments[0].id}/ship`,
    method: 'POST',
    body: JSON.stringify(data),
    headers: getHeaders(context),
  }));

  const setIn = (key: string, newValue: string) =>
    setFulfillment(
      Object.assign({}, fulfillment, {
        [key]: newValue,
      }),
    );

  if (state.loading) {
    return <Loader />;
  }

  return (
    <Page
      title="Order Details"
      subtitle={`#${order.order_number} — Placed on ${dateFormat(
        order.created_at,
        DATE_FORMAT,
      )}`}
    >
      {state.error && <ErrorFlash error={state.error} />}
      {state.complete && !state.error && (
        <Flash text="Order has been updated" />
      )}
      <div className="flex justify-center ml-auto mr-auto">
        <Link
          to={`/orders/${order.id}`}
          className="f5 link br-pill bg-near-white hover-bg-black-10 ph4 pv3 mh2 b black"
        >
          ← Back to Order
        </Link>
      </div>
      {/* TODO: Conditional "already shipped. Back to order." */}
      <form onSubmit={() => submit(fulfillment)}>
        <Section title="Delivery details">
          <div className="flex">
            <div className="flex-auto w-100 w-50-ns pr3">
              <Text
                label="Carrier Name"
                field={['carrier_name']}
                id="carrier_name"
                defaultValue={fulfillment.carrier_name}
                placeholder="i.e. DHL"
                onValueChange={setIn}
              />
              <TextArea
                label="Notes"
                field={['notes']}
                id="notes"
                defaultValue={fulfillment.notes}
                placeholder="Any information that the customer might need"
                onValueChange={setIn}
              />
            </div>
            <div className="flex-auto w-100 w-50-ns pl3">
              <Text
                label="Tracking Code"
                field={['tracking_code']}
                id="tracking_code"
                defaultValue={fulfillment.tracking_code}
                placeholder="i.e. DHL00010001001"
                onValueChange={setIn}
              />
              <Text
                label="Tracking URL"
                field={['tracking_url']}
                id="tracking_url"
                defaultValue={fulfillment.tracking_url}
                placeholder="Link to the website for users to track the delivery"
                onValueChange={setIn}
              />
            </div>
          </div>
        </Section>
        <ActionBar
          className={ActionBarStyle.alignCenter}
          actions={[
            {
              className: ButtonStyle.Confirm,
              label: 'Ship',
              handler: () => submit(fulfillment),
            },
          ]}
        />
      </form>
    </Page>
  );
}
